import { useState } from "react";
import { useCookies } from "react-cookie";
import type { FieldValues } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { useNavigate, useParams } from "react-router";
import * as Sentry from "@sentry/react";

import {
  authenticationErrorMessageMap,
  AuthenticationErrors,
  useAuthentication,
} from "@lib/authentication";
import type {
  SignInOutcome,
  TotpSetup,
} from "@lib/authentication/hooks/use-authentication/use-authentication.types";

export const TOTP_WRONG =
  "That code did not work. Check your authenticator app and try the newest code.";
export const TOTP_EXPIRED =
  "The sign-in timed out before the code arrived. Enter your password again.";
export const FIRST_SIGN_IN_EXPIRED =
  "The sign-in timed out. Sign in again; if you already chose a new password, use that one.";
export const NEW_PASSWORD_WEAK =
  "Use at least 12 characters, with upper and lower case letters, a number and a symbol.";
export const NEW_PASSWORD_MISMATCH = "The two passwords do not match.";
export const PASSWORD_UPDATED = "Password updated. Sign in with it.";
export const CODE_REQUESTS_LIMITED =
  "Too many attempts, wait a few minutes and try again.";

/** Where the sign-in form is (OMG-2777). After the password: the
 *  authenticator code, or on a first sign-in to the admin pool a new
 *  password and then the authenticator setup. */
export type LoginStep =
  | "password"
  | Exclude<SignInOutcome["step"], "signed-in">;

/** `onStartOver` replaces the password form when the steps are reached from
 *  somewhere else (the reset form, whose code is used up): it gets the
 *  message to show next to a fresh sign-in. */
export const useLoginForm = (
  onSuccess: () => void,
  onStartOver?: (message: string) => void,
) => {
  const navigate = useNavigate();
  const { signIn, continueSignIn, resetPassword } = useAuthentication();
  const [, setCookie] = useCookies(["rememberMe"]);
  const [errors, setErrors] = useState<Record<string, any>>({});
  const { t } = useTranslation();
  const params = useParams();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [step, setStep] = useState<LoginStep>("password");
  const [totpSetup, setTotpSetup] = useState<TotpSetup>();
  // The address being signed in, to label the account in the authenticator.
  const [email, setEmail] = useState("");

  // Signed in, or show the next step. Returns true when a step is left.
  const applyOutcome = (outcome: SignInOutcome) => {
    if (outcome.step === "signed-in") {
      onSuccess();
      return false;
    }
    setErrors({});
    setTotpSetup(outcome.step === "totp-setup" ? outcome.setup : undefined);
    setStep(outcome.step);
    return true;
  };

  // Cognito drops a half-finished sign-in after a few minutes; start over.
  const startOver = () => {
    const message = step === "totp" ? TOTP_EXPIRED : FIRST_SIGN_IN_EXPIRED;
    if (onStartOver) return onStartOver(message);
    setStep("password");
    setTotpSetup(undefined);
    setErrors({ submit: { type: "custom", message } });
  };

  const login = async ({ email, password, rememberMe }: FieldValues) => {
    try {
      setIsSubmitting(true);
      setCookie("rememberMe", rememberMe ? "true" : "false");
      setEmail(email);
      applyOutcome(await signIn({ email: email, password }));
      return;
    } catch (error) {
      const errorEventProps = { ...params, error };

      if (error instanceof Error) {
        if (
          error.message === AuthenticationErrors.INVALID_CREDENTIALS ||
          error.message === AuthenticationErrors.LOCKED_ACCOUNT
        ) {
          const errorMessage = authenticationErrorMessageMap[error.message];
          return setErrors((prev) => ({
            ...prev,
            email: {
              type: "custom",
              message: t(errorMessage),
            },
          }));
        }
        errorEventProps.error = error.message;
        // A Cognito step this form does not handle (OMG-2777), e.g. a reset
        // password: the admin only sees the generic error, so report it.
        if (error.message.startsWith("Unexpected sign-in step")) {
          Sentry.captureException(error);
        }
      }

      setErrors((prev) => ({
        ...prev,
        submit: t("js.checkout.serverError"),
      }));
    } finally {
      setIsSubmitting(false);
    }
  };

  const forgotPassword = async ({ email }: FieldValues) => {
    try {
      setIsSubmitting(true);
      const normalizedEmail = email.toLowerCase();
      // Cognito is the source of truth for accounts now, and resetPassword
      // validates the email itself (unknown -> UserNotFound / silent per the
      // pool's user-existence-error setting), so the old verifyUser email-exists
      // pre-check (legacy GraphQL) is redundant and was dropped with OMG-2092.
      await resetPassword(normalizedEmail);
      return navigate(
        `/reset-password?email=${encodeURIComponent(normalizedEmail)}`,
        {
          state: { showResetPasswordEmailNotification: true },
        },
      );
    } catch (error) {
      const errorProps = { ...params, error };

      if (error instanceof Error) {
        errorProps.error = error.message;
        if (error.message === AuthenticationErrors.LIMIT_EXCEEDED_EXCEPTION) {
          return setErrors({
            submit: { type: "custom", message: CODE_REQUESTS_LIMITED },
          });
        }
      }

      setErrors((prev) => ({
        ...prev,
        submit: t("js.checkout.serverError"),
      }));
    } finally {
      setIsSubmitting(false);
    }
  };

  // The authenticator code, at every sign-in or as the first code from a
  // freshly scanned authenticator (step "totp-setup").
  const submitTotp = async ({ totpCode }: FieldValues) => {
    try {
      setIsSubmitting(true);
      applyOutcome(
        await continueSignIn(String(totpCode ?? "").replace(/\s/g, ""), email),
      );
    } catch (error) {
      if (
        error instanceof Error &&
        error.message === AuthenticationErrors.INVALID_CODE
      ) {
        return setErrors({ totpCode: { type: "custom", message: TOTP_WRONG } });
      }
      startOver();
    } finally {
      setIsSubmitting(false);
    }
  };

  // First sign-in to the admin pool: replace the emailed temporary password.
  const submitNewPassword = async ({
    newPassword,
    confirmPassword,
  }: FieldValues) => {
    if (newPassword !== confirmPassword) {
      return setErrors({
        confirmPassword: { type: "custom", message: NEW_PASSWORD_MISMATCH },
      });
    }
    try {
      setIsSubmitting(true);
      applyOutcome(await continueSignIn(String(newPassword), email));
    } catch (error) {
      if (
        error instanceof Error &&
        (error.message === AuthenticationErrors.INVALID_PASSWORD ||
          error.message === AuthenticationErrors.INVALID_PARAMETER)
      ) {
        return setErrors({
          newPassword: { type: "custom", message: NEW_PASSWORD_WEAK },
        });
      }
      startOver();
    } finally {
      setIsSubmitting(false);
    }
  };

  // Returns true when the stored credentials need another step, so the
  // caller shows the form (the stored handoff cannot carry one).
  const loginByCode = async (credentials: string[]) => {
    if (credentials.length === 2) {
      setEmail(credentials[0]);
      return applyOutcome(
        await signIn({ email: credentials[0], password: credentials[1] }),
      );
    }
    return false;
  };

  // Straight after a password reset (OMG-2777): sign in with the new
  // password and carry on with whatever Cognito asks next, which in the
  // admin pool is the authenticator code (or its setup). The password is
  // already changed, so any failure here sends the admin to sign in with it.
  const signInAfterReset = async (email: string, password: string) => {
    try {
      setEmail(email);
      applyOutcome(await signIn({ email, password }));
    } catch (error) {
      if (
        error instanceof Error &&
        error.message.startsWith("Unexpected sign-in step")
      ) {
        Sentry.captureException(error);
      }
      if (onStartOver) return onStartOver(PASSWORD_UPDATED);
      setErrors({ submit: { type: "custom", message: PASSWORD_UPDATED } });
    }
  };

  return {
    loginByCode,
    signInAfterReset,
    login,
    submitTotp,
    submitNewPassword,
    step,
    totpSetup,
    errors,
    isSubmitting,
    forgotPassword,
  };
};
